/**
 * Super Admin Dashboard Page
 * 
 * Main entry point for super admin management features.
 * Only accessible by users with is_super_admin = true.
 * 
 * Features three tabs:
 * 1. Super Admins - Read-only list of super admin users
 * 2. Tenants - Create and manage organization tenants
 * 3. Users - Manage auth users, create profiles, assign tenants
 */

import { useAuth } from '@/core/AuthContext';
import { useLanguage } from '@/core/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Shield, Building2, Users, Lock } from 'lucide-react';
import { SuperAdminList } from '../components/SuperAdminList';
import { TenantList } from '../components/TenantList';
import { AuthUsersList } from '../components/AuthUsersList';

export default function SuperAdminDashboard() {
  const { profile, loading } = useAuth();
  const { t } = useLanguage();

  // Show loading state while auth is being determined
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-pulse text-muted-foreground">
          {t('common.loading', 'Loading...')}
        </div>
      </div>
    );
  }

  // Access denied for non-super admins
  if (!profile?.is_super_admin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <Lock className="h-12 w-12 mx-auto text-destructive mb-2" />
            <CardTitle>{t('superadmin.accessDenied.title', 'Access Denied')}</CardTitle>
            <CardDescription>
              {t(
                'superadmin.accessDenied.description',
                'Only super administrators can access this area. If you believe this is an error, please contact your system administrator.'
              )}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          {t('superadmin.dashboard.title', 'Super Admin Dashboard')}
        </h1>
        <p className="text-muted-foreground">
          {t(
            'superadmin.dashboard.description',
            'Manage system-wide settings, tenants, and users'
          )}
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="admins" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="admins" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('superadmin.tabs.admins', 'Admins')}
            </span>
          </TabsTrigger>
          <TabsTrigger value="tenants" className="gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('superadmin.tabs.tenants', 'Tenants')}
            </span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t('superadmin.tabs.users', 'Users')}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="admins" className="space-y-4">
          <SuperAdminList />
        </TabsContent>

        <TabsContent value="tenants" className="space-y-4">
          <TenantList />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <AuthUsersList />
        </TabsContent>
      </Tabs>
    </div>
  );
}
